/**
 * DriverOpsPanel Component
 * Key metrics + quick actions for driver dashboard
 * Shows Next Stops with pickup/delivery addresses and Navigate buttons
 * Maximum 2 packages displayed
 * Now includes automatic GPS tracking for live delivery updates
 */
import { useState, useEffect, useMemo } from "react";
import { formatAddress, getStopDestination } from "../lib/formatAddress";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import api from "../lib/api";
import QRScanner from "./QRScanner";
import PackageChat from "./PackageChat";
import DriverReturnTasks from "./DriverReturnTasks";
import { useDriverGPS } from "../hooks/useDriverGPS";
import {
  QrCode,
  Package,
  Truck,
  DollarSign,
  TrendingUp,
  MapPin,
  Clock,
  CheckCircle,
  AlertTriangle,
  Navigation,
  Loader2,
  Map,
  MessageCircle,
  Phone,
  ArrowRight,
  Wifi,
  WifiOff
} from "lucide-react";

const MAX_STOPS_DISPLAY = 2;

const DriverOpsPanel = ({ onJobUpdate }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPickupScanner, setShowPickupScanner] = useState(false);
  const [showDeliverScanner, setShowDeliverScanner] = useState(false);
  const [nextStops, setNextStops] = useState([]);
  const [activeChatJob, setActiveChatJob] = useState(null);

  // Extract tracking codes from active stops for GPS tracking
  const activeTrackingCodes = useMemo(() => {
    return nextStops
      .filter(s => s.tracking_code || s.tracking_id)
      .map(s => s.tracking_code || s.tracking_id);
  }, [nextStops]);

  // GPS tracking hook - auto-sends location for active deliveries
  const { 
    location, 
    isTracking, 
    error: gpsError, 
    lastUpdate,
    startTracking, 
    stopTracking 
  } = useDriverGPS(activeTrackingCodes, 10000);

  // Auto-start GPS when there are active deliveries
  useEffect(() => {
    if (activeTrackingCodes.length > 0 && !isTracking) {
      startTracking();
      toast.success("Live tracking started for " + activeTrackingCodes.length + " delivery(s)");
    } else if (activeTrackingCodes.length === 0 && isTracking) {
      stopTracking();
    }
  }, [activeTrackingCodes.length, isTracking, startTracking, stopTracking]);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 15000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const [statsRes, jobsRes, urgentRes] = await Promise.all([
        api.get("/driver/stats").catch(() => ({ data: {} })),
        api.get("/drivers/jobs").catch(() => ({ data: { assigned: [], available: [] } })),
        api.get("/urgent-offers/my-accepted").catch(() => ({ data: { offers: [] } }))
      ]);
      
      setStats(statsRes.data);
      
      const regularJobs = jobsRes.data.assigned || [];
      const urgentOffers = urgentRes.data.offers || [];
      
      const urgentJobs = urgentOffers
        .filter(o => o.status === "accepted" || o.status === "picked_up" || o.status === "in_transit")
        .map(o => ({
          id: o.id,
          package_id: o.package_id,
          tracking_id: o.package_id,
          tracking_code: o.tracking_code,
          pickup_address: o.pickup_address,
          dropoff_address: o.dropoff_address,
          driver_fee: o.driver_earnings,
          delivery_fee: o.offer_amount,
          sender_name: o.sender_name,
          recipient_name: o.recipient_name,
          status: o.picked_up ? "in_transit" : "pending_pickup",
          pickup_deadline: o.pickup_deadline,
          is_urgent: true,
          is_hot: o.is_hot,
          label: o.label
        }));
      
      const allJobs = [...urgentJobs, ...regularJobs].slice(0, MAX_STOPS_DISPLAY);
      setNextStops(allJobs);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePickupScan = async (qrData) => {
    try {
      const response = await api.post("/driver/scan/pickup", {
        qr_data: qrData,
        lat: location?.latitude,
        lng: location?.longitude,
        device_info: navigator.userAgent
      });
      
      toast.success(response.data.message || "Pickup scanned successfully!");
      setShowPickupScanner(false);
      fetchStats();
      if (onJobUpdate) onJobUpdate();
    } catch (error) {
      throw new Error(error.response?.data?.detail || "Scan failed");
    }
  };

  const handleDeliverScan = async (qrData) => {
    try {
      const response = await api.post("/driver/scan/deliver", {
        qr_data: qrData,
        lat: location?.latitude,
        lng: location?.longitude,
        device_info: navigator.userAgent
      });
      
      toast.success(response.data.message || "Delivery completed!");
      setShowDeliverScanner(false);
      fetchStats();
      if (onJobUpdate) onJobUpdate();
    } catch (error) {
      throw new Error(error.response?.data?.detail || "Scan failed");
    }
  };

  const openNavigationToAddress = (address) => {
    if (!address) {
      toast.error("Address not available");
      return;
    }
    
    // Use lat/lng for precision if available
    if (address.lat && address.lng) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${address.lat},${address.lng}&travelmode=driving`, "_blank");
      return;
    }
    
    let addressStr = "";
    if (typeof address === "string") {
      addressStr = address;
    } else {
      const parts = [address.street, address.city, address.parish, "Jamaica"].filter(Boolean);
      addressStr = parts.join(", ");
    }
    
    if (!addressStr || addressStr.length < 5) {
      toast.error("Invalid address");
      return;
    }
    
    const encoded = encodeURIComponent(addressStr);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encoded}&travelmode=driving`, "_blank");
  };

  const openMapsRoute = () => {
    if (!nextStops || nextStops.length === 0) {
      toast.info("No stops to navigate to");
      return;
    }

    // Build stops using lat/lng for precision when available
    const getStopCoords = (stop) => {
      // Determine which address to use based on status
      const isPendingPickup = ["pending", "accepted", "assigned"].includes(stop.status);
      // Prioritize from_location/to_location (objects with lat/lng) over string addresses
      const addr = isPendingPickup ? (stop.from_location || stop.pickup_address) : (stop.to_location || stop.dropoff_address);
      
      // Use lat/lng if available for precision
      if (addr?.lat && addr?.lng) {
        return `${addr.lat},${addr.lng}`;
      }
      
      // Fallback to formatted address
      const formatted = getStopDestination({ pickup_address: stop.pickup_address, dropoff_address: stop.dropoff_address }) || formatAddress(addr);
      return formatted && formatted.length > 3 ? formatted : null;
    };

    const stopCoords = nextStops.map(getStopCoords).filter(Boolean);

    if (stopCoords.length === 0) {
      toast.error("Destination address missing");
      return;
    }

    // Use driver's current GPS if available, otherwise let Google Maps determine current location
    const destination = stopCoords[0];
    const waypoints = stopCoords.slice(1, 8);

    const params = new URLSearchParams({ api: "1", destination, travelmode: "driving" });
    
    // Add origin only if we have driver's GPS location
    if (location?.latitude && location?.longitude) {
      params.set("origin", `${location.latitude},${location.longitude}`);
    }
    
    if (waypoints.length) params.set("waypoints", waypoints.join("|"));

    window.open(`https://www.google.com/maps/dir/?${params.toString()}`, "_blank");
  };

  const getCongestionColor = (level) => {
    switch(level?.toLowerCase()) {
      case "low": return "text-green-400 bg-green-500/20";
      case "medium": return "text-amber-400 bg-amber-500/20";
      case "high": return "text-red-400 bg-red-500/20";
      default: return "text-slate-400 bg-slate-500/20";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Return-to-sender tasks (highlighted when a customer cancels post-pickup) */}
      <DriverReturnTasks />
      {/* GPS Status Bar */}
      {activeTrackingCodes.length > 0 && (
        <div className={`rounded-lg px-4 py-2 flex items-center justify-between ${isTracking ? "bg-green-500/20 border border-green-500/50" : "bg-red-500/20 border border-red-500/50"}`}>
          <div className="flex items-center gap-2">
            {isTracking ? (
              <Wifi className="h-4 w-4 text-green-400 animate-pulse" />
            ) : (
              <WifiOff className="h-4 w-4 text-red-400" />
            )}
            <span className={`text-sm font-medium ${isTracking ? "text-green-400" : "text-red-400"}`}>
              {isTracking ? "LIVE TRACKING ACTIVE" : "GPS OFFLINE"}
            </span>
            {location && (
              <span className="text-xs text-slate-400 ml-2">
                ({location.latitude?.toFixed(4)}, {location.longitude?.toFixed(4)})
              </span>
            )}
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={isTracking ? stopTracking : startTracking}
            className={isTracking ? "text-red-400 hover:text-red-300" : "text-green-400 hover:text-green-300"}
          >
            {isTracking ? "Stop" : "Start"}
          </Button>
        </div>
      )}

      {gpsError && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg px-4 py-3 text-red-400 text-sm flex items-center justify-between gap-3" data-testid="gps-error">
          <span>GPS Error: {gpsError}</span>
          <Button
            size="sm"
            onClick={startTracking}
            className="bg-green-600 hover:bg-green-500 text-white shrink-0"
            data-testid="enable-location-btn"
          >
            Enable Location
          </Button>
        </div>
      )}

      {/* Quick Actions Row */}
      <div className="grid grid-cols-3 gap-3">
        <Button onClick={() => navigate("/driver/scan?tab=pickup&code=" + encodeURIComponent(activeTrackingCodes[0] || ""))} className="h-16 bg-green-600 hover:bg-green-500 flex flex-col items-center justify-center gap-1" data-testid="scan-pickup-btn">
          <QrCode className="h-6 w-6" />
          <span className="text-xs font-medium">Scan Pickup</span>
        </Button>
        
        <Button onClick={() => navigate("/driver/scan?tab=deliver&code=" + encodeURIComponent(activeTrackingCodes[0] || ""))} variant="outline" className="h-16 border-amber-500 text-amber-500 hover:bg-amber-500/10 flex flex-col items-center justify-center gap-1" data-testid="scan-deliver-btn">
          <Package className="h-6 w-6" />
          <span className="text-xs font-medium">Scan Drop-off</span>
        </Button>
        
        <Button onClick={openMapsRoute} variant="outline" className="h-16 border-blue-500 text-blue-500 hover:bg-blue-500/10 flex flex-col items-center justify-center gap-1" data-testid="open-map-btn">
          <Map className="h-6 w-6" />
          <span className="text-xs font-medium">Open Map</span>
        </Button>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-400 mb-1">
              <Truck className="h-4 w-4" /><span className="text-xs">Active</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats?.active_deliveries || nextStops.length || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-400 mb-1">
              <Package className="h-4 w-4" /><span className="text-xs">Pending</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats?.pickups_pending || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-400 mb-1">
              <CheckCircle className="h-4 w-4" /><span className="text-xs">Today</span>
            </div>
            <p className="text-2xl font-bold text-green-400">{stats?.completed_today || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-400 mb-1">
              <DollarSign className="h-4 w-4" /><span className="text-xs">Earnings</span>
            </div>
            <p className="text-2xl font-bold text-amber-400">J${stats?.today_earnings?.toLocaleString() || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-400 mb-1">
              <TrendingUp className="h-4 w-4" /><span className="text-xs">Avg/Pkg</span>
            </div>
            <p className="text-2xl font-bold text-white">J${stats?.avg_per_delivery?.toLocaleString() || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-400 mb-1">
              <AlertTriangle className="h-4 w-4" /><span className="text-xs">Traffic</span>
            </div>
            <Badge className={`${getCongestionColor(stats?.congestion)} border-0`}>{stats?.congestion || "—"}</Badge>
          </CardContent>
        </Card>
      </div>

      {/* Next Stops Widget */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <Navigation className="h-4 w-4 text-amber-500" />
              Next Stops ({Math.min(nextStops.length, MAX_STOPS_DISPLAY)} of {nextStops.length})
            </h3>
            {nextStops.length > 0 && (
              <Button size="sm" onClick={openMapsRoute} className="bg-blue-600 hover:bg-blue-500 text-white flex items-center gap-1" data-testid="navigate-all-btn">
                <Navigation className="h-4 w-4" />Navigate All
              </Button>
            )}
          </div>

          {nextStops.length === 0 ? (
            <p className="text-slate-500 text-center py-4">No pending stops - Accept a job to see your route</p>
          ) : (
            <div className="space-y-3">
              {nextStops.slice(0, MAX_STOPS_DISPLAY).map((stop, index) => {
                const isPendingPickup = !stop.status || stop.status === "pending_pickup" || stop.status === "accepted" || stop.status === "pending";
                const currentAddress = isPendingPickup 
                  ? (stop.from_location || stop.pickup_address) 
                  : (stop.to_location || stop.dropoff_address);
                const currentLabel = isPendingPickup ? "PICKUP" : "DELIVER";
                const currentName = isPendingPickup ? stop.sender_name : stop.recipient_name;
                
                return (
                  <div key={stop.id || stop.tracking_id || index} className={`rounded-xl border-2 overflow-hidden ${stop.is_hot ? "border-orange-500 bg-gradient-to-r from-orange-950/50 to-slate-800/50" : stop.is_urgent ? "border-red-500 bg-gradient-to-r from-red-950/50 to-slate-800/50" : "border-slate-700 bg-slate-800/50"}`}>
                    <div className={`px-4 py-2 flex items-center justify-between ${stop.is_hot ? "bg-orange-500/20" : stop.is_urgent ? "bg-red-500/20" : isPendingPickup ? "bg-amber-500/20" : "bg-green-500/20"}`}>
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${stop.is_hot ? "bg-orange-500 text-white" : stop.is_urgent ? "bg-red-500 text-white" : isPendingPickup ? "bg-amber-500 text-black" : "bg-green-500 text-white"}`}>{index + 1}</div>
                        <span className={`text-xs font-bold ${stop.is_hot ? "text-orange-400" : stop.is_urgent ? "text-red-400" : isPendingPickup ? "text-amber-400" : "text-green-400"}`}>{currentLabel}</span>
                        {(stop.tracking_code || stop.package_id) && (
                          <div className="flex items-center gap-1">
                            {stop.tracking_code && <span className="text-xs font-bold bg-emerald-500/30 text-emerald-300 px-1.5 py-0.5 rounded">#{stop.tracking_code}</span>}
                            {stop.package_id && <span className="text-xs text-slate-400">{stop.package_id}</span>}
                          </div>
                        )}
                      </div>
                      <span className="text-lg font-bold text-green-400">J${(stop.driver_fee || stop.delivery_fee || 0).toLocaleString()}</span>
                    </div>
                    
                    <div className="p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <MapPin className={`h-5 w-5 mt-0.5 ${isPendingPickup ? "text-amber-400" : "text-green-400"}`} />
                        <div className="flex-1">
                          <p className="text-sm text-white font-medium">{formatAddress(currentAddress) || "Address pending"}</p>
                          {currentName && <p className="text-xs text-slate-400 mt-1">{isPendingPickup ? "From:" : "To:"} {currentName}</p>}
                        </div>
                      </div>
                      
                      {isPendingPickup && stop.dropoff_address && (
                        <div className="flex items-center gap-2 text-xs text-slate-500 pl-8">
                          <ArrowRight className="h-3 w-3" />
                          <span>Then deliver to: {formatAddress(stop.dropoff_address)?.substring(0, 40)}...</span>
                        </div>
                      )}
                      
                      <div className="flex gap-2 pt-2">
                        <Button size="sm" onClick={() => openNavigationToAddress(currentAddress)} className={`flex-1 ${isPendingPickup ? "bg-amber-600 hover:bg-amber-500" : "bg-green-600 hover:bg-green-500"} text-white flex items-center justify-center gap-2`} data-testid={`navigate-stop-${index}`}>
                          <Navigation className="h-4 w-4" />Navigate to {currentLabel}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setActiveChatJob(stop)} className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10" title="Chat">
                          <MessageCircle className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {nextStops.length > MAX_STOPS_DISPLAY && (
                <p className="text-center text-slate-500 text-sm">+{nextStops.length - MAX_STOPS_DISPLAY} more stops</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {showPickupScanner && <QRScanner title="Scan Pickup" subtitle="Scan the package QR code" onScan={handlePickupScan} onClose={() => setShowPickupScanner(false)} manualPlaceholder="Enter tracking ID" />}
      {showDeliverScanner && <QRScanner title="Scan for Delivery" subtitle="Scan recipient phone QR or PUDO QR" onScan={handleDeliverScan} onClose={() => setShowDeliverScanner(false)} manualPlaceholder="Enter tracking ID" />}
      {activeChatJob && <PackageChat trackingCode={activeChatJob.tracking_id || activeChatJob.tracking_code || activeChatJob.package_id} chatType="recipient_driver" onClose={() => setActiveChatJob(null)} floating={true} />}
    </div>
  );
};

export default DriverOpsPanel;
